// MessageRow — one message in a stream (spec §5 rules 4, 9, 10).
//
// The body is the largest, most-alive text on this page — not grey subtext under
// a sender name. Sender and timestamp are muted labels. An agent-proposed message
// is visually distinct: violet tint + a "Proposed" label + the agent's name +
// Release + Edit (the human releases it to the client or returns it to draft).
// writer_id null → "Insider Perks", never guess a person.
import React from "react";
import { formatWhen } from "../../shared/format";
import { linkify } from "../../shared/linkify";
import MessageAttachments, { extractInlineImages, extractMarkdownFiles, extractInlineVideos } from "./MessageAttachments";

const PROPOSED_SURFACE =
  "border border-violet-200 dark:border-violet-900/50 bg-violet-50/70 dark:bg-violet-900/10";

/**
 * @param {object} props
 * @param {object} props.message   { id, from, writer_id, body, at, is_agent, status, client_facing }
 * @param {() => void} [props.onRelease]  Release an agent-proposed message (approve/send).
 * @param {() => void} [props.onEdit]     Return an agent-proposed message to draft.
 */
export default function MessageRow({ message, onRelease, onEdit }) {
  const m = message || {};
  const isProposed = m.is_agent && m.status === "proposed";
  const from = m.from || "Insider Perks"; // writer_id null → "Insider Perks", never guess

  // ClickUp embeds uploaded images (![](url)), .md documents ([name.md](url)),
  // and videos ([name.mov](url)) as markdown in the body. Pull all three out so
  // the body renders without the dead "!Screenshot….png" / link text and the files
  // show as tappable thumbnails below the message (image → image viewer, .md →
  // in-app markdown reader, video → in-app video player).
  const { body: noImages, images: inlineImages } = extractInlineImages(m.body || "");
  const { body: noMd, files: markdownFiles } = extractMarkdownFiles(noImages);
  const { body: cleanBody, videos: inlineVideos } = extractInlineVideos(noMd);

  return (
    <div
      data-testid="message-row"
      data-message-id={m.id}
      className={`rounded-lg px-3 py-2.5 ${isProposed ? PROPOSED_SURFACE : ""}`}
    >
      <div className="flex items-baseline gap-2">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{from}</span>
        {isProposed && (
          <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-violet-600 dark:text-violet-400">
            Proposed
          </span>
        )}
        {m.at && (
          <span className="ml-auto shrink-0 text-xs text-gray-400 tabular-nums">
            {formatWhen(m.at)}
          </span>
        )}
      </div>
      <p className="mt-1 text-base text-gray-900 dark:text-gray-100">{linkify(cleanBody)}</p>
      <MessageAttachments message={m} inlineImages={inlineImages} markdownFiles={markdownFiles} inlineVideos={inlineVideos} />
      {isProposed && (onRelease || onEdit) && (
        <div className="mt-2 flex items-center gap-2">
          {onEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white px-2.5 py-1 rounded-md border border-gray-200 dark:border-gray-700"
              data-testid="message-edit"
            >
              Edit
            </button>
          )}
          {onRelease && (
            <button
              type="button"
              onClick={onRelease}
              className="text-sm font-medium text-white bg-violet-600 px-2.5 py-1 rounded-md hover:bg-violet-700"
              data-testid="message-release"
            >
              Release
            </button>
          )}
        </div>
      )}
    </div>
  );
}
